/** @module csi/table/Excel */
define([
	"dojo/_base/declare",
	"dojo/dom-class",
	"dojo/query",
	"jszip",
	"underscore",
	"csi",
	"csi/_base/Deferred",
	"csi/action/Controller",
	"csi/comm/PostRequest",
	"csi/html",
	"csi/locale",
	"csi/model/image",
	"csi/state",
	"csi/table",
	"csi/util",
	"csi/utility/stringUtility"
], function(
	declare, 
	domClass, 
	query, 
	JSZip, 
	_, 
	csi, 
	Deferred, 
	Controller, 
	PostRequest, 
	html, 
	locale, 
	{getSizeByImageSize}, 
	state, 
	csiTable, 
	util,
	{purifyText}
) {

	/**
	 * @typedef {Object} ExcelChunk
	 * Each object represents an amount of work to do to export to Excel
	 * @property {boolean} bQuery - true if need to query for data
	 * @property {string[]} resultIds - query result ids
	 * @property {Level} levelId - query level id
	 */

	/**
	 * @typedef {Object} ExcelRowData
	 * Each object represents a row in the table
	 * @property {boolean} bNew - true if query result did not exist before this operation
	 * @property {Context} ctx - context to use when rendering child rows
	 * @property {QueryResult} qr - query result
	 * @property {string} rowHtml - HTML output for row
	 * @property {string} firstCellHtml - HTML output for first cell which is needed to flatten hierarchy
	 */

	var BLACK_COLOR = "rgb(0, 0, 0)";
	var TEXT_COLOR = "rgb(74, 74, 74)";
	var PRUNE_CLASSES = [html.CSS_ICON, html.CSS_NO_EXPORT];
	var RE_SPACE = / /g;
	var TD = "td";
	var TH = "th";
	var TR = "tr";
	var TRANSPARENT = "transparent";
	var TRANSPARENT_COLOR = "rgba(0, 0, 0, 0)";
	var XML_NAMESPACE = 'xmlns="http://www.w3.org/1999/xhtml"';

	/**
	 * Return true if element uses any CSS class in the classNames array
	 * @param {DOMNode} e
	 * @param {string[]} classNames
	 * @return {boolean}
	 */
	function _containsAnyCSSClass(e, classNames) {
		for (var i = 0; i < classNames.length; i++) {
			if (domClass.contains(e, classNames[i])) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Return information to pass to the server to be written to the server log
	 * @param {csi.view.Widget} view
	 * @return {Object}
	 */
	function _getLogParams(view) {
		var defView = view.defView;

		return {
			CustomViewURL: view.get("customViewId"),
			Locale: locale.getLocale(),
			ViewNode: view.getViewURL(),
			ViewId: defView.getId(),
			ViewName: defView.getName()
		}
	}

	/**
	 * Return true if row has single child that spans whole row
	 * @param {DOMNode} tr
	 * @return {boolean}
	 */
	function _isSpannerRow(tr) {
		return (tr.firstChild == tr.lastChild) && (tr.firstChild.colSpan > 1);
	}

	/**
	 * Transform map of query level to query results into chunks to process by limit
	 * @param {Object<string,string[]>} mapLevelToRows
	 * @param {ExcelChunk[]>} chunks
	 * @param {number} limit
	 * @param {boolean} bNew
	 */
	function _mapToChunks(mapLevelToResults, chunks, limit, bNew) {
		_.each(mapLevelToResults, function(resultIds, levelId) {
			var n = resultIds.length;

			for (var i = 0; i < n; i += limit) {
				chunks.push({
					bQuery: bNew,
					levelId: levelId,
					resultIds: resultIds.slice(i, Math.min(n, i + limit))
				});
			}
		});
	}

	/**
	 * Convert element and children so it is ready for export
	 * @param {DOMNode} e
	 * @return {boolean} return false to delete the element
	 */
	function _processElement(e) {
		var tag = e.nodeName.toLowerCase();

		if (e.nodeType == 1) {
			// delete elements that contain certain classes
			if (_containsAnyCSSClass(e, PRUNE_CLASSES)) {
				return false;
			}

			// delete elements with prefixes (not supported by Excel)
			if (e.nodeName.indexOf(":") >= 0) {
				return false;
			}

			var style = getComputedStyle(e);

			// prune hidden elements
			if (style.opacity === 0 || style.display === "none" || style.visibility === "hidden") {
				if ((tag == TD) && (tag == TH)) {
					html.clear(e);
					return true;
				} else {
					return false;
				}
			}

			// handle background-color, background-image, color and text-align styles for tr/th/td
			if ((tag == TR) || (tag == TH) || (tag == TD)) {
				var backgroundColor = style.backgroundColor;
				if (backgroundColor != TRANSPARENT && backgroundColor != TRANSPARENT_COLOR) {
					e.style.backgroundColor = backgroundColor;
				}

				// populate the column width
				if (tag == TH) {
					e.style.width = style.width + html.PX;
				}

				var color = style.color;
				if (color !== BLACK_COLOR && color != TEXT_COLOR) {
					e.style.color = color;
				}

				var backgroundImage = style.backgroundImage;
				if (backgroundImage != "none") {
					e.style.backgroundImage = backgroundImage;
				}

				// if font weight is bold, then apply to element
				var fontWeight = style.fontWeight;
				if (fontWeight == "bold" || (!!Number(fontWeight) && (Number(fontWeight) >= 600))) {
					e.style.fontWeight = "bold";
				}

				e.style.textAlign = style.textAlign;
			}
		} else if (util.isTextNode(e)) {
			var s = e.nodeValue;

			if (s && (s.indexOf(" ") >= 0) && (getComputedStyle(e.parentNode).whiteSpace == "pre")) {
				html.create("span", {
					innerText: s.replace(RE_SPACE, csi.EMPTY_CHAR)
				}, e, "replace");
			}

			//if the node is date time and value contains ", " then replace with " "
			if (e.parentNode?.classList.contains("attrTime")) {
				if (s.includes(", ")) {
					e.nodeValue = s.replace(", ", " ");
				}
			}

			return true;
		}

		// process all child nodes
		var children = e.childNodes;

		for (var i = children.length - 1; i >= 0; --i) {
			if (!_processElement(children[i])) {
				e.removeChild(children[i]);
			}
		}

		return true;
	}

	/**
	 * Serialize the DOM node into an XML format and get rid of namespace
	 * @param {DOMNode} tr
	 * @param {XMLDocument} xmlDocument
	 * @param {XMLSerializer} serializer
	 * @return {string}
	 */
	function _serializeRow(tr, xmlDocument, serializer) {
		var xmlDom = xmlDocument.importNode(tr, true);
		var s = serializer.serializeToString(xmlDom);
		return s.replace(XML_NAMESPACE, "");
	}

	return declare(Controller, {
		/** @property {number} excelResultsPerTimeout - number of results to process per timeout so as to avoid browser lock */
		excelResultsPerTimeout: 200,

		/** @property {number} _excelMaxLevel - max hierarchy level for row in view */
		_excelMaxLevel: 1,

		/** @property {Object<string,ExcelRowData>} _excelRowMap - map of the query result id to object holding information about row to export */
		_excelRowMap: null,

		/** @property {string[]} _excelRowIds - array holding ordered query result ids for rows to export */
		_excelRowIds: null,

		/** @property {XMLSerializer} _excelSerializer - converts DOMNode to XML string */
		_excelSerializer: null,

		/** @property {DOMNode} _excelTable - table where rows can be inserted for processing */
		_excelTable: null,

		/** @property {XMLDocument} _excelXmlDocument - document used to serialize content */
		_excelXmlDocument: null,

		/** @property {number} _expandLevel - level to expand table to even if auto-expand is not enabled */
		_expandLevel: null,

		/**
		 * @override
		 * Display error if exporting flat and multiple matrices in view; otherwise, show progress dialog and start processing
		 */
		preProcess: function() {
			var view = this.widget;

			this.displayProgressDialog({
				closable: false,
				progressValue: 0
			});

			_.defer(function(self) {
				self.when(view.refreshDataIfNewlyCreated(), function() {
					self.process();
				});
			}, this);
		},

		/**
		 * @override
		 * Start processing to query, render, and publish HTML content for Excel
		 */
		process: function() {
			// create table where export rows will be inserted; absolutely position so they are out of the view
			var view = this.widget;
			var sourceTable = view.getHeadTableNode();	// doesn't matter which table since both will have same CSS classes applied
			var table = html.create("table", {
				className: sourceTable.className + " csi-offscreen"
			});
			html.create("thead", null, table);
			html.create("tbody", null, table);
			view.domNode.appendChild(table);

			this._excelTable = table;
			this._expandLevel = this._getLevelToExpandTo();

			// create objects needed for XML serialization
			this._excelSerializer = new XMLSerializer();
			this._excelXmlDocument = new DOMParser().parseFromString("<xml/>", "text/xml");

			// calculate chunks of work
			var chunks = this._mapResults();

			// create sequence of work; for progress values, assume 10% to start processing and 20% to publish to server,
			// and twice as much work is needed for chunks that query the server than not
			var sequence = [() => this._reportStartOfExportToServer()];
			var startingProgressValue = 10;
			var serverProgressValue = 20;
			var queryRatio = 2;
			var total = 100 - startingProgressValue - serverProgressValue;
			var progressSteps = 0;

			for (const chunk of chunks) {
				progressSteps += chunk.bQuery ? queryRatio : 1;
			}

			var progressStep = total / progressSteps;
			var progressValue = startingProgressValue;

			for (const chunk of chunks) {
				progressValue += (chunk.bQuery ? queryRatio : 1) * progressStep;
				sequence.push(() => this._processChunk(chunk, Math.round(progressValue) / 100));
			}

			sequence.push(() => this.publish());

			this.displayProgressValue(startingProgressValue / 100);
			this._processSequence(sequence);
		},

		/**
		 * Submit FormData
		 * @param {FormData} form
		 * @param {string} viewName
		 * @return {Deferred}
		 */
		_submitFormData: function(form, viewName) {
			var self = this;
			var ioArgs = {
				headers: {},
				data: form,
				handleAs: "blob"
			};

			return new PostRequest().post(ioArgs).then(function(response) {
				var filename;

				// parse 'Content-Disposition' header to get to the filename
				var contentDisposition = response.getHeader('Content-Disposition');
				if (contentDisposition) {
					var stringToSearch = 'filename="';
					var startIndex = contentDisposition.indexOf(stringToSearch);
					if (startIndex >= 0) {
						startIndex += stringToSearch.length;
						var endIndex = contentDisposition.indexOf('"', startIndex);
						if (endIndex >= 0) {
							filename = contentDisposition.substring(startIndex, endIndex);
						} else {
							filename = contentDisposition.substring(startIndex);
						}

						filename = decodeURIComponent(filename);
					}
				}
				html.download(filename || (viewName + '.xlsx'), response.data);
				self.postProcess(true)
			});
		},

		/**
		 * @override
		 * Construct the full HTML and submit to server
		 */
		publish: function() {
			var self = this;
			var view = this.widget;
			var {height, width} = getSizeByImageSize(view.imageSize);

			// "tableView" has special meaning in Java, so include; but it is not needed for styling
			var output = '<html>' +
				'<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />' +
				'<body>' +
				`<table class="tableView" maxheight="${height}" maxwidth="${width}">` +
				'<thead>' + purifyText(this._processHead()) + '</thead>' +
				'<tbody>' + purifyText(this._processBody()) + '</tbody>' +
				'</table>' +
				'</body>' +
				'</html>';

			// reset class members and variables as they are no longer needed in hopes of reducing memory usage
			html.orphan(this._excelTable);
			this._excelTable = null;

			// debugging
			var bDebug = state.get("debugMode");
			if (bDebug) {
				console.warn(output);
				//				var temp = window.open("", "temp");
				//				temp.document.write(output);
				//				temp.document.close();
			}

			var viewName = view.defView.getName();
			var form = new FormData();

			form.append("Module", "SiteAdmin");
			form.append("Operation", "ConvertHTMLToExcel");
			form.append("Name", viewName);

			_.each(_getLogParams(view), function(value, key) {
				if (value != null) {
					form.append(key, value);
				}
			});

			if (bDebug) {
				form.append("RemoveCreationDate", true);
			}

			const zip = new JSZip();
			zip.file("view.html", output);

			const dfd = zip.generateAsync({
				type: "blob",
				compression: "DEFLATE",
				compressionOptions: {
					level: 6
				}
			});

			this.when(dfd, blob => {
				form.append("ContentCompressed", blob);
				return self._submitFormData(form, viewName);
			});
		},

		/**
		 * Unmerge all colspan and rowspan cells in given table row
		 * @param {DOMNode[]} rows
		 */
		_flatten: function(rows) {
			// process colspans first
			_.each(rows, function(tr) {
				query("> [colspan]", tr).forEach(function(e) {
					var x = parseInt(e.colSpan);
					var e2;

					while (x-- > 1) {
						e2 = e.cloneNode(true);
						e2.colSpan = 1;
						e.parentNode.insertBefore(e2, e);
					}

					e.colSpan = 1;
				});
			});

			// process one row at a time for rowspans to make sure indexing is correct
			// FIX: this does not work in case with two matrices of different size
			_.each(rows, function(tr, i) {
				query(">[rowspan]", tr).forEach(function(e) {
					var x = parseInt(e.rowSpan);
					if (x > 1) {
						var e2 = e.cloneNode(true);
						e2.rowSpan = x - 1;
						e.rowSpan = 1;

						var n = html.getElementIndex(e);
						var nextRow = rows[i + 1];

						if (n >= nextRow.childNodes.length) {
							nextRow.appendChild(e2);
						} else {
							nextRow.insertBefore(e2, nextRow.childNodes[n]);
						}
					}
				});
			});
		},

		/**
		 * Return minimum level to expand table to as we query for more. The custom view may not be set to auto-expand,
		 * but could have been expanded using the toolbar.
		 * @return {number}
		 */
		_getLevelToExpandTo() {
			// getLevelToExpand will return null if nothing left to expand, so to get the max level the table is expanded to we can use getLevelToCollapse
			const {widget} = this;
			const expandLevel = widget.getLevelToExpand();

			if (expandLevel != null) {
				return expandLevel;
			}

			const collapseLevel = widget.getLevelToCollapse();
			if (collapseLevel != null) {
				// collapse level is always 1 less than expand level
				return collapseLevel + 1;
			}

			return 0;
		},

		/**
		 * Build maps of all query results to export to Excel
		 */
		_mapResults: function() {
			const {widget, _expandLevel} = this;
			var qrRoot = widget.getQueryResult();
			var rowIds = this._excelRowIds = [];
			var rowMap = this._excelRowMap = {};
			var newMap = {};
			var oldMap = {};
			var maxLevel = 1;

			function add(qr, bNew) {
				// track new query result
				rowIds.push(qr.id);

				rowMap[qr.id] = {
					bNew,
					ctx: null,
					qr,
					firstCellHtml: "",
					rowHtml: ""
				};

				csi.objectArrayAddValue(bNew ? newMap : oldMap, qr.level.id, qr.id);
			}

			function create(url, params) {
				// create query result for given url
				const createParams = Object.assign({url}, params);
				const qr = widget._createQueryResult(createParams);
				const ql = qr.level;

				add(qr, true);

				// if level is set to auto-expand for a group, then expand
				if (ql.bAutoExpand || (_expandLevel > qr.hl)) {
					params = getChildParams(qr);

					if (ql.childLevel.groupSpec) {
						// if second level of grouping, then query store (which should already be cached)
						widget._query(qr).then(results => {
							results.forEach(item => create(item.$URL, params));
						});
					} else {
						// if lowest level of grouping, then get urls from group
						qr.getGroupUrls()?.forEach(url => create(url, params));
					}
				}
			}

			function getChildParams(qrParent) {
				// return parameters for child query results of given query result
				const {hl, level, url} = qrParent;
				const {childLevel} = level;
				const limit = widget.getQueryLimitIfReached(qrParent);
				const bReport = Boolean(childLevel.groupSpec && childLevel.report);

				return {
					bReport,
					bResultNode: true,
					generateReport: bReport && !limit,
					hl: hl + 1,
					level: childLevel,
					parentResult: qrParent,
					parentUrl: url
				};
			}

			function traverse(qrParent) {
				const {childResults} = qrParent;
				let firstResult;

				// add displayed child results
				_.each(childResults, function(qr) {
					if (!qr.bHidden && !qr.bAction) {
						firstResult = firstResult || qr;
						add(qr, false);
						traverse(qr);
					}
				});

				if (firstResult) {
					// track that there are results at this level
					maxLevel = Math.max(maxLevel, firstResult.hl + 1);

					// add results for More children
					if (qrParent.bMore && !firstResult.level.bExportNoMore) {
						const params = getChildParams(qrParent);
						const store = qrParent.getStore();
						const moreUrls = store.getResults().slice(qrParent.nChildren);

						moreUrls.forEach(url => create(url, params));
					}
				}
			}

			// build list of objects to represent displayed rows of table, including More results and root result if visible
			if (widget.isShowingRootNode()) {
				add(qrRoot, false);
			} else {
				// still add root result to map so context can be tracked and reused
				rowMap[qrRoot.id] = {
					qr: qrRoot,
					ctx: null
				};
			}

			traverse(qrRoot);

			// track max hierarchy level in view
			this._excelMaxLevel = maxLevel;

			// finally, divide work to do into chunks to process next
			var chunks = [];
			var limit = this.excelResultsPerTimeout;

			_mapToChunks(oldMap, chunks, limit);
			_mapToChunks(newMap, chunks, limit, true);

			// make sure chunks are processed with highest level first, or else "flatten" functionality will not work;
			// firstCellHtml or parent result must be known before processing children
			var levelMap = csi.hashArray(widget._queryLevels, "id", Number);

			chunks = csi.sort(chunks, function(chunk) {return levelMap[chunk.levelId];});

			return chunks;
		},

		/**
		 * Generate HTML output string for table body rows
		 * @return {string}
		 */
		_processBody: function() {
			var view = this.widget;
			var bodyContent = _.map(this._excelRowIds, function(id) {
				var rowData = this[id];
				var s = rowData.rowHtml;

				// remove new query results
				if (rowData.bNew) {
					view._removeQueryResult(rowData.qr, false, false, true);
				}

				rowData.rowHtml = null;
				return s;
			}, this._excelRowMap);

			// clear members as they are no longer needed
			this._excelRowIds = null;
			this._excelRowMap = null;

			return bodyContent.join("");
		},

		/**
		 * Process one chunk of work to query, render, and process results to HTML for export
		 * @param {ExcelChunk} chunk
		 * @param {number} progressValue
		 * @return {Deferred}
		 */
		_processChunk: function(chunk, progressValue) {
			const view = this.widget;
			var rowMap = this._excelRowMap;
			var newNodeMap = {};
			var cache;
			var dfd;
			var store;

			const queryResults = _.map(chunk.resultIds, id => {
				var qr = rowMap[id].qr;

				if (qr.parentResult) {
					// parentResult does not exist for root result
					store = store || qr.parentResult.getStore();
				}

				return qr;
			});

			// query for data if results were not previously rendered
			if (chunk.bQuery) {
				var urls = _.pluck(queryResults, "url");
				var request = store.createNodesRequest && store.createNodesRequest(urls);
				if (request) {
					dfd = request.go().then(res => {
						// track new nodes in response
						cache = store.cachingStore;

						_.each(res.getNodes(), node => {
							if (!cache.get(node.$URL)) {
								newNodeMap[node.$URL] = 1;
							}
						});

						cache.cacheResponse(res);
					});
				}
			}

			dfd = dfd || new Deferred().resolve();

			// if reporting, then ensure that report rows are calculated
			if (view.bReporting) {
				const reportResults = queryResults.filter(qr => qr.generateReport);

				if (reportResults.length) {
					dfd = dfd.then(() => view.calculateReportRows(reportResults));
				}
			}

			// render results and extract resulting HTML
			dfd = dfd.then(() => {
				// switch state to indicate excel export in progress in case rendering strategy needs to change
				state.set("excel", true);
				_.each(queryResults, this._processResult, this);
				state.set("excel", false);

				// remove newly added nodes from cache
				_.each(newNodeMap, (value, url) => {
					cache.remove(url);
				});
			});

			// update progress
			return dfd.then(() => this.displayProgressValue(progressValue));
		},

		/**
		 * Generate table header rows and return HTML content
		 * @return {string}
		 */
		_processHead: function() {
			state.set("excel", true);

			var rows = this.widget._buildHeadRows();
			var thead = this._excelTable.firstChild;
			var maxLevel = this._excelMaxLevel;
			var serializer = this._excelSerializer;
			var xmlDoc = this._excelXmlDocument;
			var s;

			state.set("excel", false);

			return _.reduce(rows, function(memo, tr) {
				thead.appendChild(tr);

				if (_processElement(tr)) {
					// make first column span levels of hierarchy except the matrix row
					if (maxLevel > 1 && !csiTable.isMergedRow(tr)) {
						tr.firstChild.colSpan = maxLevel;
					}

					s = _serializeRow(tr, xmlDoc, serializer);
				} else {
					s = "";
				}

				thead.removeChild(tr);

				return memo + s;
			}, "");
		},

		/**
		 * Process first row column according to view hierarchy and flat option
		 * @param {ExcelRowData} rowData
		 * @param {DOMNode} tr
		 * @param {number} hlMax
		 * @param {boolean} bFlat
		 * @param {boolean} bSpanner
		 */
		_processHierarchy: function(rowData, tr, hlMax, bFlat, bSpanner) {
			var cell = tr.firstChild;
			var hl = rowData.qr.hl;
			var bGroup = rowData.qr.isGroup;

			if (bFlat) {
				// create cells for each parent
				var rowMap = this._excelRowMap;
				var qrParent;
				var i;

				if (bGroup || !bSpanner) {
					qrParent = rowData.qr.parentResult;
				}

				for (i = hl - 1; i >= 0; --i) {
					while (qrParent && (qrParent.hl != i)) {
						qrParent = qrParent.parentResult;
					}

					html.create(TD, {
						innerText: qrParent ? rowMap[qrParent.id].firstCellHtml : null,
						rowSpan: cell.rowSpan,
						style: "text-align: left"
					}, tr, "first");
				}

				// add extra cells if not at lowest level
				var nExtra = hlMax - hl - 1;

				if (bSpanner) {
					tr.lastChild.colSpan += nExtra;
				} else {
					for (i = nExtra; i > 0; --i) {
						html.create(TD, {
							rowSpan: cell.rowSpan,
							style: "text-align: left"
						}, cell, "after");
					}
				}
			} else {
				// if not flat, create preceeding cells to indent up to hierarchy level
				cell.colSpan += hlMax - (hl + 1);

				if (hl > 0) {
					html.create(TD, {
						colSpan: hl,
						rowSpan: cell.rowSpan,
						style: "text-align: left"
					}, cell, "before");
				}
			}
		},

		/**
		 * Process DOM rows for given row data and store resulting HTML
		 * @param {QueryResult} qr
		 */
		_processResult: function(qr) {
			const {bCompareNode, id, parentResult, url} = qr;

			// do not process comparison rows
			if (bCompareNode) {
				return;
			}

			var rowData = this._excelRowMap[id];
			var rows = [];
			var ctx;
			var parentRowData;

			if (parentResult) {
				parentRowData = this._excelRowMap[parentResult.id];
				ctx = parentRowData.ctx;
			}

			if (!ctx) {
				if (parentResult) {
					// use and store context from parent so it can be reused for all child rows of parent
					ctx = parentRowData.ctx = parentResult.getChildContext();
				} else {
					// root row uses own context
					ctx = qr.getContext();
				}

				ctx.setReadOnly(true);
				ctx.preRollup();
				// ctx.postRollup is not needed as we do not need to track rollup dependencies for new data in view
			}

			ctx.pushParams({urlNode: url});
			ctx.view._generateResultRow(qr, rows, ctx);
			ctx.pop();

			if (!rows.length) {
				// nothing to output
				rowData.rowHtml = "";
				return;
			}

			var bFlat = this.m_action.m_bFlat;
			var tbody = this._excelTable.lastChild;
			var serializer = this._excelSerializer;
			var xmlDocument = this._excelXmlDocument;

			// add rows to table body
			_.each(rows, function(tr) {
				tbody.appendChild(tr);
			});

			// process rows so they are ready for export
			_.each(rows, _processElement);

			// extract first cell content which is needed for "flat" option
			var firstRow = rows[0];

			rowData.firstCellHtml = firstRow.firstChild.innerText;

			// process row hierarchy in conjunction with flat option
			var bSpanner = _isSpannerRow(firstRow);

			if (this._excelMaxLevel > 1) {
				this._processHierarchy(rowData, firstRow, this._excelMaxLevel, bFlat, bSpanner);
			}

			// flatten rows if so configured, although skip processing of spanner rows;
			// even non-hierarchical tables need to be flattened if there are matrices
			if (bFlat && !bSpanner) {
				this._flatten(rows);
			}

			// extract HTML content
			rowData.rowHtml = _.reduce(rows, function(memo, tr) {
				return memo + _serializeRow(tr, xmlDocument, serializer);
			}, "");

			// remove rows from table body
			_.each(rows, function(tr) {
				tbody.removeChild(tr);
			});
		},

		_processSequence: function(/*Function[]*/functions) {
			// summary: apply functions in sequence, each one can optionally return a Deferred to delay the next step; also,
			// each step is deferred so that browser will not lock up
			let i = 0;

			const next = () => {
				const f = functions[i++];

				this.when(f(), () => {
					// use defer in between to make sure browser does not show unresponsive warning
					_.defer(() => {
						if (functions[i + 1]) {
							// if there is more than one function remaining, call next
							next();
						} else {
							// if only one function remains, then call it
							functions[i]();
						}
					});
				});
			}

			next(null);
		},

		/**
		 * Let the server know some additional information before we start to export
		 * @return {Deferred}
		 */
		_reportStartOfExportToServer: function() {
			var params = _getLogParams(this.widget);
			var request = new PostRequest("SiteAdmin", "ConvertHTMLToExcelStarted", params);
			return request.go();
		}
	});
});